import React, { useState } from "react"
import ReactDOM from "react-dom"
import { useDraggable } from "@dnd-kit/core"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import InfoOutlined from "@mui/icons-material/InfoOutlined"
import CallMerge from "@mui/icons-material/CallMerge"
import DeleteOutline from "@mui/icons-material/DeleteOutline"

import { capitalizeFirstLetter } from "../../utils"
import { COLORS } from "../../utils"
import {
  getTilePosition,
  getHandleStyle,
  getTileHeightMetrics,
} from "../../utils/prodPlanner"
import { getTileVisuals } from "../../utils/tiles"
import { createResizeHandler } from "../../actions/ProdPlanner/prodPlanner"
import { BLOCK_HEIGHT, UNASSIGNED_TILE_CONTENT_WIDTH } from "../../utils/constant"
import { useTooltip } from "../../hooks/useTooltip"
import { useContextMenu } from "../../hooks/useContextMenu"

const ProdPlannerTile = ({ tile, isExpanded, onToggle, onResize, isInOverlay, tiles, setTiles, isAnyTileDragging }) => {
  const [isResizing, setIsResizing] = useState(false)

  // Custom hooks
  const { showInfo, tooltipPosition, infoIconRef, infoTooltipRef, handleInfoMouseEnter, handleInfoMouseLeave, handleTooltipMouseEnter, handleTooltipMouseLeave } = useTooltip()
  const { contextMenu, hoveredMenuItem, setHoveredMenuItem, setIsHoveringTile, setIsHoveringMenu, handleDuplication, handleOtherTileMouseEnter, handleContextMenu: handleContextMenuEvent, handleDelete } = useContextMenu()
  
  // Use tile's recipe expansion for merged recipes
  const [isRecipeExpanded, setIsRecipeExpanded] = useState(false)

  // Always use draggable for all tiles
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: tile.objectId })

  // Dynamic style for tile background based on tile properties
  const { color: backgroundColor, icon: tileIcon, border } = getTileVisuals(tile, true)
  const pseTileStyle = { ...styles.tileWrapper, backgroundColor: backgroundColor, border: border }

  // Check if tile is assigned
  const isAssigned = tile.start != null && tile.end != null && tile.machine != null

  // Render unassigned tile content (reusable for both unassigned tiles and tooltip)
  const renderUnassignedContent = (forTooltip = false) => (
    <div>
      <div style={{  display: "flex", justifyContent: "space-between", alignItems: "start"}}>
        <div style={styles.uniqueCode}>
          {!tile.isStepMergedDict ? 
            tile.uniqueCode
            : 
            <>
              <div style={styles.recipeHeader}>
                <span>{Object.keys(tile.isStepMergedDict).length} recettes</span>
                <span 
                  style={{cursor: "pointer", height: "24px", width: "24px", color: COLORS.BLACK_600 }} 
                  onClick={() => setIsRecipeExpanded(v => !v)}>
                  {isRecipeExpanded ? <ExpandLess style={{ fontSize: "24px" }}/> : <ExpandMore style={{ fontSize: "24px" }}/>}
                </span>
              </div>
              {isRecipeExpanded && tile.isStepMergedDict && (
                <div>
                  {Object.entries(tile.isStepMergedDict).map(([uniqueCode, item], idx) => (
                    <div key={idx}>
                      <span>{uniqueCode}</span>
                      <span style={styles.recipeDetail}> - {item.sectionName}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          }
        </div>
        {tileIcon}
      </div>
      {!tile.isStepMergedDict && <div style={styles.uniqueCode}>{capitalizeFirstLetter(tile.sectionName)}</div>}
      <div style={styles.pseName}>{capitalizeFirstLetter(tile.PSEName)}</div>
      
      <div style={styles.tileContent}>
        <div style={styles.tileLabel}>
          <span>Poids brut</span>
          <span style={styles.tileValue}>
            {tile.theoreticalGrossWeight ? `${(Math.round(tile.theoreticalGrossWeight * 100) / 100).toLocaleString("fr-FR")} kg` : "0 kg"}
          </span>
        </div>

        <div style={styles.tileLabel}>
          <span>Poids net</span>
          <span style={styles.tileValue}>
            {tile.theoreticalNetWeight ? `${(Math.round(tile.theoreticalNetWeight * 100) / 100).toLocaleString("fr-FR")} kg` : "0 kg"}
          </span>
        </div>
      </div>

      {(forTooltip || isExpanded) && (
        <div style={styles.expandedContent}>
          <div style={styles.tileContent}>
            <div style={styles.tileLabel}>
              <span>Barquettage</span>
              <span style={styles.tileValue}>
                {tile.packagingDate && typeof tile.packagingDate === "number" && !isNaN(tile.packagingDate) 
                ? new Date(tile.packagingDate).toLocaleDateString("fr-FR") 
                : "-"}
              </span>
            </div>
          </div>
          
          <div style={styles.tileContent}>
            <div style={styles.tileLabel}>Les instructions</div>
            <div style={styles.tileValue}>
              {tile.description}
            </div>
          </div>
          
          <div style={styles.tileContent}>
            <div style={styles.tileLabel}>Ingrédients</div>
            <ul style={styles.ingredientsList}>
              {tile.ingredients && tile.ingredients.length > 0 ? (
                tile.ingredients.map((ingredient, idx) => {
                  return (
                    <li key={idx} style={styles.ingredient}>
                      <span style={styles.ingredientName}>{capitalizeFirstLetter(ingredient.name)}</span>
                    </li>
                  )
                })
              ) : (<li>Aucun ingrédient</li>)}
            </ul>
          </div>
        </div>
      )}
      
      {/* Only show "voir plus" button for actual unassigned tiles, not in tooltip */}
      {!forTooltip && (
        <div style={styles.seeMoreContainer(tile.originalId)}>
          {tile.originalId && <DeleteOutline 
            sx={{ fontSize: "24px", cursor: "pointer" }}
            onClick={() => handleDelete(tile, tiles, setTiles)}
          />}
          <div style={styles.seeMore} onClick={onToggle}>
            {isExpanded ? "Voir moins" : "Voir plus"}
            {isExpanded ? <ExpandLess style={styles.seeMoreIcon} /> : <ExpandMore style={styles.seeMoreIcon}/>}
          </div>
        </div>
      )}
    </div>
  )

  // Calculate tile height and available space for labels
  const { tileHeight, contentPadding, maxLines } = getTileHeightMetrics(tile)

  if (!isAssigned) {
    return (
      <div 
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        data-id={tile.objectId}
        onMouseEnter={handleOtherTileMouseEnter}
        style={{
          ...pseTileStyle,
          position: "relative",
          opacity: isDragging ? 0 : 1,
        }}
      >
        {renderUnassignedContent(false)}
      </div>
    )
  }

  // Use the same background color logic as unassigned tiles
  const assignedTileStyle = { 
    ...styles.assignedTile, 
    backgroundColor: backgroundColor,
    border: border,
    opacity: tile.isPreview ? 0.5 : 1, // Make preview tiles semi-transparent
  }

  return (
    <div
      ref={isInOverlay ? undefined : setNodeRef}
      {...(isInOverlay ? {} : attributes)}
      data-id={tile.objectId}
      onContextMenu={(e) => handleContextMenuEvent(e, isAssigned)}
      onMouseEnter={() => setIsHoveringTile(true)}
      onMouseLeave={() => setIsHoveringTile(false)}
      style={{
        ...(isInOverlay 
          ? { 
              height: (tile.end - tile.start) * BLOCK_HEIGHT,
              width: "100%",
              borderRadius: "4px",
            } 
          : getTilePosition(tile, isDragging)
        ),
        opacity: isDragging ? 0 : 1,
        transition: isResizing ? "none" : undefined,
      }}
    >
      <div style={assignedTileStyle}>
        <div
          onMouseDown={createResizeHandler({
            direction: "top",
            tile,
            setIsResizing,
            onResize,
          })}
          style={getHandleStyle("top", backgroundColor)}
        />

        <div {...(showInfo ? {} : listeners)} style={{...styles.assignedTileContent, padding: contentPadding}}>
          <div style={styles.assignedTileHeader}>
            <div style={styles.assignedTileTextContainer}>
              <div style={styles.plannerHeader}>
                <div style={styles.plannerUniqueCode}>
                  {tile.isStepMergedDict ? 
                  <span>{Object.keys(tile.isStepMergedDict).length} recettes</span>
                  : tile.uniqueCode}
                </div>
                <div style={{
                  ...styles.plannerCombinedText,
                  WebkitLineClamp: maxLines,
                }}>
                  <span style={styles.plannerSectionName}>
                    {tile.isStepMergedDict
                      ? Object.entries(tile.isStepMergedDict).map(([uniqueCode, item], idx) => (
                          <div key={idx}>
                            <span>{uniqueCode}</span>
                            <span> - {item.sectionName}</span>
                          </div>
                        ))
                      : <>{capitalizeFirstLetter(tile.sectionName)} <br/></>
                    }
                  </span>
                  <span style={styles.plannerPseName}>{capitalizeFirstLetter(tile.PSEName)}</span>
                </div>
              </div>
                <div 
                  ref={infoIconRef}
                  style={styles.infoIcon(tileHeight)}
                  onMouseEnter={handleInfoMouseEnter}
                  onMouseLeave={handleInfoMouseLeave}
                >
                  <InfoOutlined style={{ fontSize: tileHeight <= BLOCK_HEIGHT ? "12px" : "24px"}} />
                </div>
            </div>
            
            {/* Render tooltip using Portal to escape parent overflow constraints */}
            {showInfo && isAnyTileDragging === false && ReactDOM.createPortal(
              <div 
                ref={infoTooltipRef}
                style={{
                  ...styles.infoTooltip,
                  ...tooltipPosition || {},
                  backgroundColor: backgroundColor, 
                }}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
              >
                {renderUnassignedContent(true)}
              </div>,
              document.body
            )}
          </div>
        </div>

        <div
          onMouseDown={createResizeHandler({
            direction: "bottom",
            tile,
            setIsResizing,
            onResize,
          })}
          style={getHandleStyle("bottom", backgroundColor)}
        />
      </div>
      
      {/* Context Menu */}
      {contextMenu && ReactDOM.createPortal(
        <div
          style={styles.contextMenu(contextMenu, hoveredMenuItem)}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => setIsHoveringMenu(true)}
          onMouseLeave={() => setIsHoveringMenu(false)}
        >
          <div 
            style={styles.contextMenuItem}
            onMouseEnter={() => setHoveredMenuItem(true)}
            onMouseLeave={() => setHoveredMenuItem(false)}
            onClick={() => handleDuplication(tile, tiles, setTiles) }
          >
            <CallMerge style={{ fontSize: "24px" }} />
            Répartir entre des machines
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ProdPlannerTile

const styles = {
  tileWrapper: {
    borderRadius: "8px",
    marginBottom: "10px",
    overflow: "hidden",
    padding: "16px",
    cursor: "move",
    boxSizing: "border-box",
    width: "100%",
  },
  tileContent: {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    padding: "12px",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
  },
  uniqueCode: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 500,
    color: COLORS.BLACK800,
  },
  recipeHeader: {
    display: "flex",
    alignItems: "center",
    gap: "4px"
  },
  recipeDetail: {
    fontWeight: 400
  },
  pseName: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    marginBottom: "12px",
    color: COLORS.BLACK800,
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
  plannerHeader: {
    width: "100%",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  plannerUniqueCode: {
    fontSize: "12px",
    lineHeight: "12px",
    fontWeight: 500,
    color: COLORS.BLACK800,
  },
  plannerCombinedText: {
    fontSize: "12px",
    lineHeight: "18px",
    color: COLORS.BLACK800,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordWrap: "break-word",
  },
  plannerSectionName: {
    fontWeight: 500,
  },
  plannerPseName: {
    fontWeight: 400,
  },
  tileLabel: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    color: COLORS.BLACK_600, 
    display: "flex",
    justifyContent: "space-between"
  },
  tileValue: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    color: COLORS.BLACK800,
    display: "flex",
    justifyContent: "space-between"
  },
  seeMore: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    color: COLORS.BLACK_700,
    cursor: "pointer",
  },
  seeMoreIcon: {
    marginLeft: "4px",
    fontSize: "24px"
  },
  seeMoreContainer: (hasOriginalId) => ({
    marginTop: "12px",
    display: "flex",
    justifyContent: hasOriginalId ? "space-between" : "flex-end"
  }),
  infoTooltip: {
    position: "fixed",
    border: `1px solid ${COLORS.BLACK_200}`,
    borderRadius: "8px",
    padding: "16px",
    marginLeft: "8px",
    boxShadow: "0 1px 5px rgba(0, 0, 0, 0.15)",
    zIndex: 9999,
    width: `${UNASSIGNED_TILE_CONTENT_WIDTH}px`,
    maxWidth: `${UNASSIGNED_TILE_CONTENT_WIDTH}px`,
    maxHeight: "100vh",
    overflowY: "auto",
    pointerEvents: "auto",
    boxSizing: "border-box",
  },
  expandedContent: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    boxSizing: "border-box",
  },
  ingredientsList: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
    color: COLORS.BLACK800,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    margin: 0,
    padding: 0
  },
  ingredient: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
  },
  ingredientName: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    flex: 1,
    minWidth: 0,
  },
  assignedTile: {
    height: "100%",
    borderRadius: "4px",
    position: "relative",
    boxSizing: "border-box",
    overflow: "hidden",
    color: "#414141",
    margin: "0px 1px"
  },
  assignedTileContent: {
    cursor: "move",
    height: "100%",
    userSelect: "none",
    position: "relative",
  },
  assignedTileHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "4px",
  },
  assignedTileTextContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%"
  },
  infoIcon: (tileHeight) => ({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: COLORS.BLACK_700,
    position: "relative",
    flexShrink: 0,
    width: "24px",
    height: tileHeight <= BLOCK_HEIGHT ? "12px" : "24px"
  }),
  contextMenu: (contextMenu, hoveredMenuItem) => ({
    position: "fixed",
    backgroundColor: "white",
    borderRadius: "4px",
    zIndex: 10000,
    width: "149px",
    padding: "8px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.30)",
    top: contextMenu?.y || 100,
    left: contextMenu?.x || 100,
    ...(hoveredMenuItem ? { backgroundColor: COLORS.BACKGROUND_LIGHT_GREY } : {})
  }),
  contextMenuItem: {
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: "24px",
    color: COLORS.BLACK_700,
    display: "flex",
    alignItems: "center",
    gap: "7px",
    transition: "background-color 0.2s ease",
  }
}
